/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import {
  findCurrentAccountMatch,
  sanitizeAccount,
  setAccountTree
} from '@ion/core/src/entities/Accounts';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {useElementResize} from '@ion/core/src/hooks/useElementResize';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {LogicalPosition} from '@ion/core/src/types/editors';
import {AccountLink, Crumb} from '@ion/core/src/types/entities';
import {smoothScrollByElement} from '@ion/core/src/utils/BrowserUtils';
import {BackButton} from '@ion/dls/src/components/buttons/BackButton';
import useMedia from '../../hooks/useMedia';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import Breadcrumb from '../breadcrumbs/Breadcrumbs';
import {defineBreadcrumbs} from '../breadcrumbs/BreadcrumbUtils';
import {Carousel} from '../carousel/Carousel';
import {IonSpinner} from '../loading/IonSpinner';
import IconRadioLabel from '../radio/IconRadioLabel';
import RadioButton from '../radio/RadioButton';
import GroupChooserRadioList, {GROUP_CHOOSER_ITEM_PREFIX} from './GroupChooserRadioList';

export interface GroupChooserNestedMenuProps {
  group: AccountLink;
  radioValue: string;
  selectedSearchValue?: AccountLink;
};

interface StyleProps {
  breadcrumbHeight: number;
};

const BREADCRUMB_CONTAINER_ID = 'ggc_breadcrumbContainer';

// the current theme's crumbs - in Inter, as the DLS breadcrumb is ...
const sharedTextStyles = {
  top           : '2px',
  fontSize      : '14px',
  position      : 'relative',
  fontFamily    : 'Inter',
  lineHeight    : '16px',
  letterSpacing : '0.5px',
  textTransform : 'capitalize',
  whiteSpace    : 'nowrap'
} as const;

const useStyles = makeStyles(theme => ({
  groupChooserCarousel : {
    width                          : '100%',
    height                         : '99%',
    [theme.breakpoints.down('sm')] : {
      height    : '97%',
      marginTop : '4px'
    }
  },
  carouselWrapper : {
    height    : ({breadcrumbHeight}: StyleProps) => `calc(100% - ${(breadcrumbHeight ?? 0) + 8}px)`,
    overflowY : 'auto'
  },
  currentCarouselWrapper : {
    height    : ({breadcrumbHeight}: StyleProps) => `calc(100% - ${(breadcrumbHeight ?? 0)}px)`,
    overflowY : 'auto',
    '& > div' : {
      borderRadius    : '12px',
      backgroundColor : dlsColor('grey6')
    }
  },
  slideWrapper : {

  },
  slide : {

  },
  slideItem : {
    '& label' : {
      width                 : '96%',
      bottom                : toggleThemeStyle('0', '14px'),
      position              : toggleThemeStyle('unset', 'relative'),
      marginLeft            : toggleThemeStyle('0', '8px'),
      marginRight           : toggleThemeStyle('0', '2px'),
      borderRadius          : toggleThemeStyle('0', '8px'),
      '&:has(.Mui-checked)' : {
        backgroundColor : toggleThemeStyle('inherit', dlsColor('paleBlue'))
      },
      '& .MuiTypography-body1' : {
        width : '100%'
      }
    }
  },
  breadcrumbWrapper : {
    display       : 'flex',
    position      : 'relative',
    paddingBottom : toggleThemeStyle('12px', '24px'),
    '& div'       : {
      maxWidth : toggleThemeStyle('calc(100vw - 52px)', '100%')
    }
  },
  activeCrumb : toggleThemeStyle({
    color : dlsColor('grey1')
  }, {
    ...sharedTextStyles,
    color      : dlsColor('linkBlue'),
    fontWeight : 700
  }),
  inactiveCrumb : toggleThemeStyle({
    color     : dlsColor('grey3'),
    '&:hover' : {
      cursor : 'pointer'
    }
  }, {
    ...sharedTextStyles,
    color      : dlsColor('linkBlue'),
    fontWeight : 500,
    '&:hover'  : {
      cursor : 'pointer'
    }
  })
}) as any);

/**
 * Carousel slide list for displaying a list of nested account options with any amount of
 * levels.
 *
 * ```typescript
 * import {GroupChooserNestedMenu} from '@ion/dls/src/components/groupchooser';
 *
 * <GroupChooserNestedMenu group={group}
 *   radioValue='Group1'
 *   selectedSearchValue='Group2' />
 *
 * ```
 *
 * @param group group that is displayed in the accordion header
 * @param radioValue value of the currently selected item from the list
 * @param selectedSearchValue user clicked on its chevron in the search results
 */
export const GroupChooserNestedMenu = ({
  group,
  radioValue,
  selectedSearchValue
}: GroupChooserNestedMenuProps) => {
  const {height: breadcrumbHeight} = useElementResize(BREADCRUMB_CONTAINER_ID);
  const classes                    = useStyles({breadcrumbHeight});

  const {
    isLegacy  : isLegacyTheme,
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const crumbClasses = {
    activeCrumb   : classes.activeCrumb,
    inactiveCrumb : classes.inactiveCrumb
  };

  const initialCrumb = {
    ...(isLegacyTheme && {
      label : translate('common:editors.shared.chooseGroup')
    }),
    click   : true,
    onClick : function() {
      handleCrumbClick(0);
    }
  };

  const activeSlide             = useRef(0);
  const rawCrumbs               = useRef([initialCrumb]);
  const slideRef                = useRef(null);
  const groupChooserCarouselRef = useRef(null);

  const [slideList, setSlideList] = useState(
    [
      <div className={classes.slideItem} key={activeSlide.current}>
        <RadioButton value={group.href}
          label={
            <IconRadioLabel label={EntityLabel.get(group)}
              onIconClick={function(e) {
                e.stopPropagation();
                e.preventDefault();
                handleNextClick(group);
              }} />
          } />
      </div>
    ]
  );

  const [crumbData, setCrumbData] = useState<Crumb[]>(
    defineBreadcrumbs({
      crumbs  : [initialCrumb],
      classes : crumbClasses
    })
  );

  const [loading, setLoading] = useState(false);
  const {isPhone}             = useMedia();

  useEffect(() => {
    const accountTree   = setAccountTree([group], true);
    const [match]       = findCurrentAccountMatch(
      [group],
      selectedSearchValue.href ? selectedSearchValue : {href: radioValue}
    );
    const initialCrumbs = [initialCrumb];
    if (match && (match.href !== group.href)) {
      setLoading(true);
      const parent = accountTree[entityId(accountTree[entityId(match)].parentId)];
      if (parent) {
        const {
          list: subsequentList,
          crumbs
        } = buildSlideList({
          account : parent,
          tree    : accountTree,
          handleNextClick
        });
        initialCrumbs.push(...(crumbs || []));
        slideList.push(...(subsequentList || []));
      }
      scrollToItem(match);
    }
    setSlideList(slideList);
    rawCrumbs.current = initialCrumbs;
    setCrumbData(defineBreadcrumbs({
      crumbs  : initialCrumbs,
      classes : crumbClasses
    }));
    setLoading(false);
    activeSlide.current = slideList.length - 1;
  }, []);

  useEffect(() => {
    slideRef.current?.scrollIntoView({
      block    : 'start',
      behavior : 'smooth'
    });
    groupChooserCarouselRef.current?.scrollIntoView({
      block : 'start'
    });
  }, [activeSlide.current]);

  const scrollToItem = item => {
    const timeout = setTimeout(() => {
      smoothScrollByElement(`${GROUP_CHOOSER_ITEM_PREFIX}${entityId(item)}`, LogicalPosition.Center);
      clearTimeout(timeout);
    }, 150);
  };

  function buildSlideList({
    account,
    tree,
    handleNextClick
  }) {
    const list = [];

    function makeSlideList({
      account,
      tree
    }) {
      if (account.subAccounts) {
        list.unshift({
          title       : account.title,
          href        : account.href,
          subAccounts : account.subAccounts
        });
      }
      if (tree[entityId(account)].parentId) {
        makeSlideList({
          account : tree[entityId(account)].parentId,
          tree
        });
      }
    };

    makeSlideList({
      account : account,
      tree
    });
    const componentList = list.map((item, i) => {
      return (
        <div key={`group-chooser-slide-item-${i}`}>
          <GroupChooserRadioList groups={item.subAccounts}
            handleNextClick={handleNextClick} />
        </div>
      );
    });
    const crumbsList = list.map((item, i) => {
      return {
        label   : EntityLabel.get(sanitizeAccount(item)),
        click   : true,
        onClick : function() {
          handleCrumbClick(i + 1);
        }
      };
    });
    return {
      list   : componentList,
      crumbs : crumbsList
    };
  }

  function handleNextClick(selectedGroup) {
    if (selectedGroup) {
      const slideListLength = slideList.length;
      const newCrumb = {
        label   : EntityLabel.get(sanitizeAccount(selectedGroup)),
        click   : true,
        onClick : function() {
          handleCrumbClick(slideListLength);
        }
      };
      const newCrumbs = [...(rawCrumbs.current || []), newCrumb];
      setCrumbData(defineBreadcrumbs({
        crumbs  : newCrumbs,
        classes : crumbClasses
      }));
      rawCrumbs.current = newCrumbs;
      slideList.push(
        <GroupChooserRadioList groups={sanitizeAccount(selectedGroup).subAccounts}
          handleNextClick={handleNextClick} />
      );
      setSlideList(slideList);
      activeSlide.current += 1;
    }
  }

  function handleCrumbClick(slide) {
    const diff = (slideList.length - 1) - slide;
    rawCrumbs.current.splice(slideList.length - diff, diff);
    setCrumbData(defineBreadcrumbs({
      crumbs  : rawCrumbs.current,
      classes : crumbClasses
    }));
    slideList.splice(slideList.length - diff, diff);
    setSlideList(slideList);
    activeSlide.current = slide;
  }

  function handleBack() {
    slideList.pop();
    setSlideList(slideList);
    rawCrumbs.current.pop();
    setCrumbData(defineBreadcrumbs({
      crumbs  : rawCrumbs.current,
      classes : crumbClasses
    }));
    activeSlide.current -= 1;
  }

  return (
    <div ref={groupChooserCarouselRef} className={classes.groupChooserCarousel}>
      {
        loading ? (
          <IonSpinner />
        ) : (
          <>
            <div id={BREADCRUMB_CONTAINER_ID} className={classes.breadcrumbWrapper}>
              <Breadcrumb icon={
                activeSlide.current
                  ? (isCurrentTheme
                    ? <BackButton />
                    : <ChevronLeft />
                  )
                  : null
              }
              itemsBeforeCollapse={isCurrentTheme ? 2 : 1}
              showBackLabel={isCurrentTheme}
              iconClick={handleBack}
              height={isPhone && '45px'}
              backLabel={translate('common:editors.shared.groupChooser')}
              style={{
                marginLeft : activeSlide.current ? 0 : '4px',
                maxHeight  : 'unset'
              }}>
                {crumbData as any}
              </Breadcrumb>
            </div>
            <div className={isCurrentTheme ? classes.currentCarouselWrapper : classes.carouselWrapper}>
              <Carousel disableTouchEvents
                index={activeSlide.current}
                slideClassName={classes.slideWrapper}
                containerStyle={{
                  maxWidth : 'calc(100vw - 52px)',
                  height   : 'fit-content'
                }}>
                {
                  slideList.map((slide, i) => {
                    return (
                      <div className={classes.slide}
                        style={{
                          marginLeft : i === 0 ? '12px' : 0,
                          height     : i === slideList.length - 1 ? 'fit-content': 0,
                          ...(isCurrentTheme && {
                            paddingTop : '30px'
                          })
                        }}
                        key={`group-chooser-slide-${i}`}
                        ref={activeSlide.current === i ? slideRef : null}>
                        {slide}
                      </div>
                    );
                  })
                }
              </Carousel>
            </div>
          </>
        )
      }
    </div>
  );
};

export default GroupChooserNestedMenu;
